import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { pendingLeadLogSchema, pendingLeadSchema } from "@/types/pendingIntake.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createResourceCollectionSelector, handleJsonApiError } from "jsonapi-zod-query";
import { z } from "zod";

const pendingLeadsSelector = createResourceCollectionSelector({
    type: "pendingLead",
    attributesSchema: pendingLeadSchema,
    documentMetaSchema: z.object({ totalRowCount: z.number() }),
});

export type PendingLeadsDocument = ReturnType<typeof pendingLeadsSelector>;
export type PendingLead = PendingLeadsDocument["data"][number];

export const useFetchPendingLeads = (enabled = true): UseQueryResult<PendingLeadsDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["pending-intake"],
        enabled,
        queryFn: async () => {
            const response = await fetch(apiUrl("/pending-intake"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
            });

            await handleJsonApiError(response);
            return await response.json();
        },
        select: pendingLeadsSelector,
        refetchOnWindowFocus: false,
    });
};

const pendingLeadLogsSelector = createResourceCollectionSelector({
    type: "pendingLeadLog",
    attributesSchema: pendingLeadLogSchema,
    documentMetaSchema: z.object({ totalRowCount: z.number() }),
});

export type PendingLeadLogsDocument = ReturnType<typeof pendingLeadLogsSelector>;
export type PendingLeadLogEntry = PendingLeadLogsDocument["data"][number];

export const useFetchPendingLeadLogs = (
    enabled: boolean,
): UseQueryResult<PendingLeadLogsDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["pending-intake-logs"],
        enabled,
        queryFn: async () => {
            const response = await fetch(apiUrl("/pending-intake/logs"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
            });

            await handleJsonApiError(response);
            return await response.json();
        },
        select: pendingLeadLogsSelector,
        refetchOnWindowFocus: false,
    });
};
